import React, { useState } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import { Link, Navigate } from 'react-router';
import { useMutation } from '@apollo/client';
import { LOG_IN } from '../../graphql/mutations/mutations';
import { Form, Input, Button, message, Card, Typography, Checkbox, Space, Divider } from 'antd';
import { AuthLayout } from '../../layouts/AuthLayout';
import { LoginFormValues } from '../../types/auth';
import { AuthWithSocial } from './AuthWithSocial';
import { IUser } from '../../types/user';

const { Title } = Typography;

const LoginForm: React.FC = () => {
  const { loggedIn, setAuthUser } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onError = (error: { message?: string }) => {
    message.error(error.message || 'Login failed');
    setLoading(false);
  };

  const onCompleted = (data: IUser) => {
    if (data?.id) {
      setAuthUser(data);
      message.success('Login successful!');
    } else {
      message.error((data as { message?: string }).message || 'Login failed');
    }
    setLoading(false);
  };

  const [login] = useMutation(LOG_IN, {
    onCompleted: data => {
      onCompleted(data?.LogIn);
    },
    onError,
  });

  if (loggedIn) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = (values: LoginFormValues) => {
    setLoading(true);
    login({
      variables: {
        email: values.email,
        password: values.password,
      },
    });
  };

  return (
    <Card hoverable={false} style={{ width: '25rem', maxWidth: '90vw' }}>
      <Title level={2}>Log in to your account</Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ email: '', password: '' }}>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}>
          <Input placeholder="your@email.com" />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[{ required: true, message: 'Please enter your password' }]}>
          <Input.Password placeholder="Your password" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 'unset' }}>
          <Checkbox style={{ float: 'left' }}>Remember me</Checkbox>
          <Link style={{ float: 'right' }} to="/forgot-password">
            Forgot password
          </Link>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="w-full"
              style={{ width: '100%', marginTop: '10px', marginBottom: '10px' }}>
              Login
            </Button>
            <div>
              Or <Link to="/register">register now!</Link>
            </div>
            <Divider />
            <AuthWithSocial
              onComplete={setAuthUser}
              onError={onError}
              onLoading={setLoading}
              successMessage="Login successful!"
              errorMessage="Login failed"
              loading={loading}
              type="login"
            />
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export const Login: React.FC = () => {
  return (
    <AuthLayout>
      <LoginForm />
    </AuthLayout>
  );
};
